import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FieldError, fieldErrorProps } from '@/components/ui/field-error';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  type FieldErrors,
  firstInvalidField,
  getFieldError,
  normalizeValidationDetails,
} from '@/lib/validationErrors';
import { useUser } from '@clerk/clerk-react';
import {
  AlertCircle,
  ArrowLeft,
  Bell,
  Briefcase,
  CheckCircle,
  Clock,
  DollarSign,
  Globe,
  Home,
  Loader2,
  Lock,
  MapPin,
  Send,
  Unlock,
  Users,
} from 'lucide-react';

// Note: Using emoji in Select options since native select doesn't support icons
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const benefits = [
  'Exclusive territory rights',
  '15% commission on affiliate subscriptions',
  '10% commission on equipment sales',
  'Marketing support and materials',
  'Direct line to HQ support',
  'Growth incentives and bonuses',
];

const agentInquiryFields = new Set([
  'name',
  'email',
  'phone',
  'territory',
  'territory_id',
  'experience',
  'current_business',
  'gym_connections',
  'message',
]);

interface Territory {
  id: string;
  name: string;
  country_code: string | null;
  region: string | null;
  status: 'locked' | 'unlocked' | 'assigned';
  waiting_list_count: number;
}

interface FormData {
  name: string;
  email: string;
  phone: string;
  territory: string;
  territoryId: string;
  experience: string;
  currentBusiness: string;
  gymConnections: string;
  message: string;
}

interface SubmitResult {
  lead_id: string;
  territory_status: string;
  on_waiting_list: boolean;
  waiting_list_position: number | null;
  message: string;
}

export default function AgentInquiry() {
  const { user } = useUser();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitResult, setSubmitResult] = useState<SubmitResult | null>(null);
  const [territories, setTerritories] = useState<Territory[]>([]);
  const [loadingTerritories, setLoadingTerritories] = useState(true);
  const [selectedTerritory, setSelectedTerritory] = useState<Territory | null>(null);
  const [showWaitingListPrompt, setShowWaitingListPrompt] = useState(false);
  const [waitingListCount, setWaitingListCount] = useState(0);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>();

  const [formData, setFormData] = useState<FormData>({
    name: user?.fullName || '',
    email: user?.primaryEmailAddress?.emailAddress || '',
    phone: '',
    territory: '',
    territoryId: '',
    experience: '',
    currentBusiness: '',
    gymConnections: '',
    message: '',
  });

  // Fetch available territories
  useEffect(() => {
    async function fetchTerritories() {
      try {
        const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787/api';
        const response = await fetch(`${API_URL}/public/territories`);
        const data = await response.json();
        if (data.success) {
          setTerritories(data.data.territories);
        }
      } catch (error) {
        console.error('Failed to load territories:', error);
      } finally {
        setLoadingTerritories(false);
      }
    }
    fetchTerritories();
  }, []);

  // Update form when user data loads
  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        name: user.fullName || prev.name,
        email: user.primaryEmailAddress?.emailAddress || prev.email,
      }));
    }
  }, [user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) =>
      prev
        ? Object.fromEntries(
            Object.entries(prev).filter(
              ([key]) =>
                key !== name && key !== name.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`)
            )
          )
        : prev
    );
  };

  const handleTerritorySelect = (territoryId: string) => {
    const territory = territories.find((t) => t.id === territoryId);
    setSelectedTerritory(territory || null);
    setShowWaitingListPrompt(false);
    setFieldErrors((errors) =>
      errors
        ? Object.fromEntries(
            Object.entries(errors).filter(
              ([field]) => field !== 'territory' && field !== 'territory_id'
            )
          )
        : undefined
    );
    setFormData((prev) => ({
      ...prev,
      territoryId,
      territory: territory?.name || '',
    }));
  };

  const handleSubmit = async (e: React.FormEvent, joinWaitingList = false) => {
    e.preventDefault();
    setIsLoading(true);
    setShowWaitingListPrompt(false);

    // Validate required fields
    if (!formData.name || !formData.email || (!formData.territoryId && !formData.territory)) {
      toast.error('Please fill in all required fields');
      setIsLoading(false);
      return;
    }

    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787/api';
      const response = await fetch(`${API_URL}/public/agent-inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || undefined,
          territory: formData.territory,
          territory_id: formData.territoryId || undefined,
          experience: formData.experience || undefined,
          current_business: formData.currentBusiness || undefined,
          gym_connections: formData.gymConnections || undefined,
          message: formData.message || undefined,
          join_waiting_list: joinWaitingList,
        }),
      });

      const data = await response.json();

      if (!data.success) {
        // Check if this is a "territory locked" response
        if (data.details?.can_join_waiting_list) {
          setWaitingListCount(data.details.waiting_list_count || 0);
          setShowWaitingListPrompt(true);
          setIsLoading(false);
          return;
        }

        const details = normalizeValidationDetails(data.details);
        const field = firstInvalidField(details);
        const fieldId =
          field === 'territory_id'
            ? 'territorySelect'
            : field?.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
        if (
          details &&
          field &&
          agentInquiryFields.has(field) &&
          fieldId &&
          getFieldError(details, field)
        ) {
          setFieldErrors(details);
          requestAnimationFrame(() => document.getElementById(fieldId)?.focus());
          return;
        }

        throw new Error(data.error || 'Failed to submit inquiry');
      }

      setSubmitResult(data.data);
      setIsSubmitted(true);
      toast.success(data.data.message || 'Inquiry submitted successfully!');
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to submit inquiry');
    } finally {
      setIsLoading(false);
    }
  };

  const handleJoinWaitingList = (e: React.FormEvent) => {
    handleSubmit(e, true);
  };

  // Success state
  if (isSubmitted && submitResult) {
    return (
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="border-b border-border">
          <div className="container mx-auto px-4 py-4 flex justify-between items-center">
            <button
              type="button"
              onClick={() => {
                // BUG-APR08-001: page is often opened in a new tab (target="_blank"
                // from RoleToggleBar), so there's no history to go back to.
                if (window.history.length > 1) navigate(-1);
                else navigate('/');
              }}
              className="inline-flex items-center font-semibold text-primary hover:text-primary/80 transition-colors"
            >
              <ArrowLeft className="mr-2 h-5 w-5" />
              Back
            </button>
            <Link
              to="/"
              className="inline-flex items-center font-semibold text-primary hover:text-primary/80 transition-colors"
            >
              <Home className="mr-2 h-5 w-5" />
              Home
            </Link>
          </div>
        </div>

        {/* Success State */}
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              <div
                className={`rounded-full p-6 ${submitResult.on_waiting_list ? 'bg-yellow-500/10' : 'bg-green-500/10'}`}
              >
                {submitResult.on_waiting_list ? (
                  <Clock className="h-16 w-16 text-yellow-500" />
                ) : (
                  <CheckCircle className="h-16 w-16 text-green-500" />
                )}
              </div>
            </div>

            <h1 className="text-3xl font-bold text-gradient-gold mb-4">
              {submitResult.on_waiting_list ? 'Added to Waiting List!' : 'Inquiry Received!'}
            </h1>

            {submitResult.on_waiting_list ? (
              <>
                <p className="text-muted-foreground text-lg mb-4">
                  You've been added to the waiting list for <strong>{formData.territory}</strong>.
                </p>
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-center gap-2 text-yellow-600 mb-2">
                    <Bell className="h-5 w-5" />
                    <span className="font-semibold">
                      Your Position: #{submitResult.waiting_list_position}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    We'll notify you at <strong>{formData.email}</strong> when this territory
                    becomes available.
                  </p>
                </div>
              </>
            ) : (
              <p className="text-muted-foreground text-lg mb-6">
                Thank you for your interest in becoming an ErgShoot agent for{' '}
                <strong>{formData.territory}</strong>. Our team will review your inquiry and get
                back to you within 2-3 business days.
              </p>
            )}

            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                We've sent a confirmation email to <strong>{formData.email}</strong>
              </p>
              <Link to="/">
                <Button>Return to Home</Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Waiting list prompt modal
  const WaitingListPrompt = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <Card className="max-w-md w-full animate-fade-in">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="rounded-full bg-yellow-500/10 p-4">
              <Lock className="h-8 w-8 text-yellow-500" />
            </div>
          </div>
          <CardTitle>Territory Not Yet Available</CardTitle>
          <CardDescription>
            <strong>{selectedTerritory?.name}</strong> is currently locked and not accepting
            applications.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-muted/50 rounded-lg p-4 text-center">
            <p className="text-sm text-muted-foreground mb-1">Current waiting list</p>
            <p className="text-2xl font-bold">{waitingListCount} people</p>
          </div>

          <p className="text-sm text-muted-foreground text-center">
            Join the waiting list to be notified when this territory opens for agents.
          </p>

          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setShowWaitingListPrompt(false)}
            >
              Choose Different Territory
            </Button>
            <Button className="flex-1" onClick={handleJoinWaitingList} disabled={isLoading}>
              {isLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Bell className="h-4 w-4 mr-2" />
              )}
              Join Waiting List
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {showWaitingListPrompt && <WaitingListPrompt />}

      {/* Header */}
      <div className="border-b border-border">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <button
            type="button"
            onClick={() => {
              // BUG-APR08-001: page is often opened in a new tab (target="_blank"
              // from RoleToggleBar), so there's no history to go back to.
              if (window.history.length > 1) navigate(-1);
              else navigate('/');
            }}
            className="inline-flex items-center font-semibold text-primary hover:text-primary/80 transition-colors"
          >
            <ArrowLeft className="mr-2 h-5 w-5" />
            Back
          </button>
          <Link
            to="/"
            className="inline-flex items-center font-semibold text-primary hover:text-primary/80 transition-colors"
          >
            <Home className="mr-2 h-5 w-5" />
            Home
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="flex justify-center mb-4">
              <div className="rounded-full bg-purple-500/10 p-4">
                <Users className="h-10 w-10 text-purple-500" />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-gradient-gold mb-2">Become an Agent</h1>
            <p className="text-muted-foreground text-lg">
              Partner with ErgShoot and grow the fitness community in your territory
            </p>
          </div>

          <div className="grid gap-8 lg:grid-cols-5">
            {/* Inquiry Form */}
            <Card className="lg:col-span-3 animate-fade-in">
              <CardHeader>
                <CardTitle>Agent Inquiry</CardTitle>
                <CardDescription>
                  Tell us about yourself and your territory interest
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Contact Information */}
                  <div className="space-y-4">
                    <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                      Contact Information
                    </h3>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="John Smith"
                          required
                          {...fieldErrorProps(fieldErrors, 'name')}
                        />
                        <FieldError errors={fieldErrors} field="name" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="john@example.com"
                          required
                          {...fieldErrorProps(fieldErrors, 'email')}
                        />
                        <FieldError errors={fieldErrors} field="email" />
                      </div>
                      <div className="space-y-2 sm:col-span-2">
                        <Label htmlFor="phone">Phone</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="(555) 123-4567"
                          {...fieldErrorProps(fieldErrors, 'phone')}
                        />
                        <FieldError errors={fieldErrors} field="phone" />
                      </div>
                    </div>
                  </div>

                  {/* Territory Selection */}
                  <div className="space-y-4">
                    <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      Territory Interest
                    </h3>

                    {loadingTerritories ? (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading territories...
                      </div>
                    ) : territories.length > 0 ? (
                      <div className="space-y-3">
                        <Label htmlFor="territorySelect">Select a Territory *</Label>
                        <Select
                          id="territorySelect"
                          value={formData.territoryId}
                          onChange={(e) => handleTerritorySelect(e.target.value)}
                          options={[
                            { value: '', label: 'Choose a territory...' },
                            ...territories.map((t) => ({
                              value: t.id,
                              label: `${t.status === 'unlocked' ? '🔓' : '🔒'} ${t.name}${t.country_code ? ` (${t.country_code})` : ''}`,
                            })),
                          ]}
                          {...fieldErrorProps(fieldErrors, 'territoryId')}
                        />
                        <FieldError errors={fieldErrors} field="territoryId" />

                        {/* Selected territory info */}
                        {selectedTerritory && (
                          <div
                            className={`rounded-lg p-3 ${
                              selectedTerritory.status === 'unlocked'
                                ? 'bg-green-500/10 border border-green-500/30'
                                : 'bg-yellow-500/10 border border-yellow-500/30'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              {selectedTerritory.status === 'unlocked' ? (
                                <>
                                  <Unlock className="h-4 w-4 text-green-500" />
                                  <span className="text-sm font-medium text-green-600">
                                    Available for applications
                                  </span>
                                </>
                              ) : (
                                <>
                                  <Lock className="h-4 w-4 text-yellow-500" />
                                  <span className="text-sm font-medium text-yellow-600">
                                    Not yet available
                                  </span>
                                  {selectedTerritory.waiting_list_count > 0 && (
                                    <Badge variant="secondary" className="ml-auto">
                                      {selectedTerritory.waiting_list_count} waiting
                                    </Badge>
                                  )}
                                </>
                              )}
                            </div>
                            {selectedTerritory.status === 'locked' && (
                              <p className="text-xs text-muted-foreground mt-1">
                                You can join the waiting list to be notified when it opens
                              </p>
                            )}
                          </div>
                        )}

                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Globe className="h-3 w-3" />
                          <span>
                            Don't see your region?{' '}
                            <button
                              type="button"
                              className="text-primary hover:underline"
                              onClick={() => {
                                setSelectedTerritory(null);
                                setFormData((prev) => ({
                                  ...prev,
                                  territoryId: '',
                                  territory: '',
                                }));
                              }}
                            >
                              Enter a custom territory
                            </button>
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Label htmlFor="territory">Desired Territory/Region *</Label>
                        <Input
                          id="territory"
                          name="territory"
                          value={formData.territory}
                          onChange={handleChange}
                          placeholder="e.g., Texas, Pacific Northwest, New York Metro"
                          required
                          {...fieldErrorProps(fieldErrors, 'territory')}
                        />
                        <FieldError errors={fieldErrors} field="territory" />
                        <p className="text-xs text-muted-foreground">
                          Specify the state, region, or metro area you're interested in
                        </p>
                      </div>
                    )}

                    {/* Custom territory input when no territory selected from list */}
                    {!formData.territoryId && territories.length > 0 && (
                      <div className="space-y-2">
                        <Label htmlFor="territory">Or enter a custom territory</Label>
                        <Input
                          id="territory"
                          name="territory"
                          value={formData.territory}
                          onChange={handleChange}
                          placeholder="e.g., Texas, Pacific Northwest, New York Metro"
                          {...fieldErrorProps(fieldErrors, 'territory')}
                        />
                        <FieldError errors={fieldErrors} field="territory" />
                      </div>
                    )}
                  </div>

                  {/* Background */}
                  <div className="space-y-4">
                    <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                      <Briefcase className="h-4 w-4" />
                      Background
                    </h3>
                    <div className="grid gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="experience">Relevant Experience</Label>
                        <Input
                          id="experience"
                          name="experience"
                          value={formData.experience}
                          onChange={handleChange}
                          placeholder="Sales, fitness industry, business development..."
                          {...fieldErrorProps(fieldErrors, 'experience')}
                        />
                        <FieldError errors={fieldErrors} field="experience" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="currentBusiness">Current Business/Role</Label>
                        <Input
                          id="currentBusiness"
                          name="currentBusiness"
                          value={formData.currentBusiness}
                          onChange={handleChange}
                          placeholder="Gym owner, sales rep, entrepreneur..."
                          {...fieldErrorProps(fieldErrors, 'currentBusiness')}
                        />
                        <FieldError errors={fieldErrors} field="currentBusiness" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="gymConnections">Gym Industry Connections</Label>
                        <Input
                          id="gymConnections"
                          name="gymConnections"
                          value={formData.gymConnections}
                          onChange={handleChange}
                          placeholder="Approximate number of gym contacts in your network"
                          {...fieldErrorProps(fieldErrors, 'gymConnections')}
                        />
                        <FieldError errors={fieldErrors} field="gymConnections" />
                      </div>
                    </div>
                  </div>

                  {/* Message */}
                  <div className="space-y-4">
                    <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                      Additional Information
                    </h3>
                    <div className="space-y-2">
                      <Label htmlFor="message">
                        Tell us more about why you want to be an agent
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Your goals, experience, and what makes you a great fit..."
                        rows={4}
                        {...fieldErrorProps(fieldErrors, 'message')}
                      />
                      <FieldError errors={fieldErrors} field="message" />
                    </div>
                  </div>

                  {/* Locked territory warning */}
                  {selectedTerritory?.status === 'locked' && (
                    <div className="flex items-start gap-3 rounded-lg border border-yellow-500/30 bg-yellow-500/5 p-4">
                      <AlertCircle className="h-5 w-5 text-yellow-500 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-yellow-600">
                          This territory is not yet available
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Submitting will add you to the waiting list. You'll be notified when it
                          opens.
                        </p>
                      </div>
                    </div>
                  )}

                  <Button type="submit" disabled={isLoading} className="w-full" size="lg">
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4 mr-2" />
                    )}
                    {selectedTerritory?.status === 'locked'
                      ? 'Join Waiting List'
                      : 'Submit Inquiry'}
                  </Button>

                  <p className="text-xs text-center text-muted-foreground">
                    {selectedTerritory?.status === 'locked'
                      ? "You'll be notified when this territory becomes available."
                      : "We'll review your inquiry and contact you within 2-3 business days."}
                  </p>
                </form>
              </CardContent>
            </Card>

            {/* Benefits */}
            <div className="lg:col-span-2 space-y-6">
              <Card
                className="border-purple-500/30 animate-fade-in"
                style={{ animationDelay: '100ms' }}
              >
                <CardHeader className="text-center pb-4">
                  <CardTitle className="text-xl">Agent Benefits</CardTitle>
                  <CardDescription>What you get as an ErgShoot agent</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {benefits.map((benefit, index) => (
                      <li
                        key={benefit}
                        className="flex items-center gap-2 text-sm animate-fade-in"
                        style={{ animationDelay: `${(index + 2) * 50}ms` }}
                      >
                        <CheckCircle className="h-4 w-4 text-purple-500 shrink-0" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              <Card className="animate-fade-in" style={{ animationDelay: '200ms' }}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-primary" />
                    Commission Structure
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-sm">Affiliate Subscriptions</span>
                    <span className="font-bold text-primary">15%</span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-sm">Equipment Sales</span>
                    <span className="font-bold text-primary">10%</span>
                  </div>
                  <p className="text-xs text-muted-foreground pt-2">
                    Commissions are paid monthly for all affiliates and sales in your territory.
                  </p>
                </CardContent>
              </Card>

              {/* Territory Status Legend */}
              <Card className="animate-fade-in" style={{ animationDelay: '250ms' }}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Globe className="h-5 w-5 text-primary" />
                    Territory Status
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Unlock className="h-4 w-4 text-green-500" />
                    <span className="text-sm">Unlocked - Open for applications</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Lock className="h-4 w-4 text-yellow-500" />
                    <span className="text-sm">Locked - Join waiting list</span>
                  </div>
                </CardContent>
              </Card>

              <Card className="animate-fade-in" style={{ animationDelay: '300ms' }}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Have Questions?</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Reach out to our partnerships team at{' '}
                    <a href="mailto:agents@ergshoot.com" className="text-primary hover:underline">
                      agents@ergshoot.com
                    </a>
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
